/* @flow */
import * as React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

// Styles
import styles from './ExpressionOfGratitudeRecipientModal.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Assets
import QuoteSvg from '!@svgr/webpack?modules!src/shared/app/expression-of-gratitude/assets/quote.svg';

// Hooks
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';

// Utils
import messages from 'src/shared/app/expression-of-gratitude/l10n/expressionOfGratitudel10n.json';
import { generatePath } from 'src/shared/app/base/route/util/routeUtils.js';
import { getTopicTitle } from '@braindate/domain/lib/topic/util';
import { getUserFullName } from '@braindate/domain/lib/user/util';
import { getBraindateTopic } from 'src/shared/domain/braindate/util/braindateUtil';
import { dismissNotification } from 'src/shared/app/notification/action/uiNotificationActions';
import { expressionOfGratitudeDetailsRoute } from 'src/shared/app/base/route/setting/routeSettings';
import {
  getExpressionOfGratitudeId,
  getExpressionOfGratitudeBraindate,
  getExpressionOfGratitudeSender,
  getExpressionOfGratitudeMessage,
  getExpressionOfGratitudeSendGift,
  getExpressionOfGratitudeUnwrapitUrl,
} from '@braindate/domain/lib/expressionOfGratitude/util/expressionOfGratitudeGetters';

// Components
import Modal from 'src/shared/app/base/modal/component/Modal';
import Heading from 'src/shared/ui/component/text/Heading';
import LinkButton from 'src/shared/ui/component/link/LinkButton';
import GratitudeSponsors from '../GratitudeSponsors';

// Types
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';
import UnwrapitGiftIcon from 'src/shared/ui/component/icon/UnwrapitGiftIcon';
import { markExpressionOfGratitudeAsRead } from 'src/shared/app/expression-of-gratitude/reducer/expressionOfGratitudeSlice';

const useStyles = createUseThemeStyles(styles);

type Props = {
  notification: Notification<>,
};

const ExpressionOfGratitudeRecipientModal = ({ notification }: Props) => {
  const { data } = notification;
  const { expressionOfGratitude } = data || {};
  /*
  |------------------------------------------------------------------------------
  | Hooks
  |------------------------------------------------------------------------------
  */

  const expressionSendGift = expressionOfGratitude
    ? getExpressionOfGratitudeSendGift(expressionOfGratitude)
    : undefined;
  const unwrapitGiftUrl = expressionOfGratitude
    ? getExpressionOfGratitudeUnwrapitUrl(expressionOfGratitude)
    : undefined;
  const hasUnwrapitGift = !!expressionSendGift && !!unwrapitGiftUrl;

  const intl = useIntl();
  const classes = useStyles({ hasUnwrapitGift });
  const dispatch = useDispatch();
  const { braindateSingular } = useEventBraindateName();

  /*
  |------------------------------------------------------------------------------
  | States
  |------------------------------------------------------------------------------
  */
  const [isOpen, setIsOpen] = React.useState(true);

  /*
  |------------------------------------------------------------------------------
  | Data
  |------------------------------------------------------------------------------
  */

  if (!expressionOfGratitude) return null;

  const message = getExpressionOfGratitudeMessage(expressionOfGratitude);
  const author = getExpressionOfGratitudeSender(expressionOfGratitude);
  const braindate = getExpressionOfGratitudeBraindate(expressionOfGratitude);
  const topic = getBraindateTopic(braindate);
  const topicTitle = getTopicTitle(topic);

  const handleAfterClose = () => {
    dispatch(dismissNotification(notification));
    dispatch(markExpressionOfGratitudeAsRead(expressionOfGratitude));
  };

  const onModalClose = () => {
    setIsOpen(false);
  };

  return (
    <Modal
      width={908}
      isOpen={isOpen}
      hideActions
      shouldCloseOnOverlayClick
      showClose
      handleClose={onModalClose}
      onAfterClose={handleAfterClose}
      contentClassName={classes.modalContent}
      actionsReversed
      content={
        <div className={classes.root}>
          <div className={classes.rootContent}>
            <div className={classes.content}>
              <h2 className={classes.description}>
                <span>{getUserFullName(author)}</span>
                {intl.formatMessage(messages.beenGranted, {
                  braindateSingular,
                })}
              </h2>
              <p className={classes.topicTitle}>{topicTitle}</p>

              <div className={classes.message}>
                <QuoteSvg
                  className={classNames(classes.quote, classes.quoteIn)}
                />
                <p>{message}</p>
                <QuoteSvg
                  className={classNames(classes.quote, classes.quoteOut)}
                />
              </div>
            </div>

            {hasUnwrapitGift && (
              <>
                <div className={classes.contentSeparator}>
                  <span className={classes.separator}></span>
                </div>
                <div
                  className={classNames(
                    classes.content,
                    classes.contentCentered,
                  )}
                >
                  <div className={classes.unwrapitGiftIconContainer}>
                    <UnwrapitGiftIcon className={classes.unwrapitGiftIcon} />
                  </div>

                  <Heading level={3} layoutClass={classes.centered}>
                    {intl.formatMessage(messages.unwrapitGift)}
                  </Heading>

                  <p className={classes.smallDescription}>
                    {intl.formatMessage(messages.unwrapitGiftDescription, {
                      braindateSingular,
                    })}
                  </p>

                  <div className={classes.descriptionSeparatorContainer}>
                    <span className={classes.descriptionSeparator} />
                  </div>

                  <p className={classes.smallDescription}>
                    <span className={classes.smallDescription}>
                      {intl.formatMessage(messages.unwrapitGiftNote)}
                    </span>
                    {intl.formatMessage(messages.unwrapitGiftNoteDescription)}
                  </p>

                  <GratitudeSponsors />
                </div>
              </>
            )}
          </div>

          <div className={classes.actions}>
            <LinkButton
              to={generatePath(expressionOfGratitudeDetailsRoute, {
                id: getExpressionOfGratitudeId(expressionOfGratitude),
              })}
              variant="primary"
            >
              {intl.formatMessage(
                hasUnwrapitGift ? messages.acceptUnwrapit : messages.accept,
              )}
            </LinkButton>
          </div>
        </div>
      }
    />
  );
};

export default React.memo<Props>(ExpressionOfGratitudeRecipientModal);
